import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import axios from 'axios';
import { isAuthenticated, getToken } from '../services/authService';
import { getMyTenants } from '../services/businessService';
import { jwtDecode } from 'jwt-decode';

const TenantContext = createContext(undefined);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const TENANT_CACHE_KEY = 'tenantData';

export const useTenant = () => useContext(TenantContext);

export const TenantProvider = ({ children }) => {

  const [tenantData, setTenantData] = useState(null);
  const [loadingTenant, setLoadingTenant] = useState(true); // Initialize as true for initial load attempt
  const [errorTenant, setErrorTenant] = useState(null);
  const [selectedTenantId, setSelectedTenantId] = useState(null);

  // Helper to fetch tenants from API if not present in token
  const fetchTenantsFromApi = async () => {
    if (localStorage.getItem('tenants_allowed')) {
      try {
        const cachedTenantsString = localStorage.getItem('tenants_allowed');
        if (cachedTenantsString) {
          const cachedTenants = JSON.parse(cachedTenantsString);
          if (Array.isArray(cachedTenants) && cachedTenants.length > 0) {
            console.log('Tenants loaded from localStorage cache');
            return cachedTenants;
          }
        }
      } catch (error) {
        console.error("Error reading tenants from localStorage:", error);
        // Proceed to fetch from API if localStorage read fails
      }
    }

    // Fetch from API if not in cache or cache read failed
    try {
      const data = await getMyTenants();
      if (data && Array.isArray(data.results)) {
        localStorage.setItem('tenants_allowed', JSON.stringify(data.results));
        return data.results;
      }
      return [];
    } catch (error) {
      console.error('Error fetching tenants from API:', error);
      return [];
    }
  };

  const getTenantIdFromToken = useCallback(async () => {
    try {
      const storedTenantId = localStorage.getItem('selected_tenant_id');
      console.log('Stored selected_tenant_id from localStorage:', storedTenantId);
      if (storedTenantId && storedTenantId !== 'undefined') {
        console.log('Using selected_tenant_id from localStorage:', storedTenantId);
        return storedTenantId;
      }

      const token = getToken();
      if (!token) {
        console.error('No access token found.');
        return null;
      }
      const decoded = jwtDecode(token);
      console.log('Decoded token:', decoded);

      let tenants = decoded && Array.isArray(decoded.tenants) ? decoded.tenants : null;
      if (!tenants || tenants.length === 0) {
        console.warn('No tenants found in token. Fetching from API...');
        tenants = await fetchTenantsFromApi();
      }
      if (!tenants || tenants.length === 0) {
        console.error('No tenants found in token or from API.');
        return null;
      }
      // if (tenants.length === 1 || tenants.length > 1) {
      //   console.log('Automatically selecting the first tenant:', tenants[0].tenant_id);
      //   localStorage.setItem('selected_tenant_id', tenants[0].tenant_id);
      //   return tenants[0].tenant_id;
      // }
      return null;
    } catch (error) {
      console.error('Error decoding token or fetching tenants:', error);
      return null;
    }
  }, []);

  // Ajout d'un listener sur l'event 'tokenChanged' pour relancer la détection du tenant et le fetch
  useEffect(() => {
    const handler = async () => {
      setLoadingTenant(true);
      const determinedTenantId = await getTenantIdFromToken();
      setSelectedTenantId(determinedTenantId);
      // fetchTenantData sera appelé automatiquement par l'autre useEffect
    };
    window.addEventListener('tokenChanged', handler);
    return () => {
      window.removeEventListener('tokenChanged', handler);
    };
  }, [getTenantIdFromToken]);

  // Effect to initialize/update selectedTenantId based on auth state and token content
  useEffect(() => {
    const checkAuth = async () => {
      const authenticated = await isAuthenticated();
      if (authenticated) {
        const determinedTenantId = await getTenantIdFromToken();
        if (selectedTenantId !== determinedTenantId) {
          setSelectedTenantId(determinedTenantId);
        }
      } else {
        if (selectedTenantId !== null) {
          setSelectedTenantId(null);
        }
      }
    };
    checkAuth();
  }, [getTenantIdFromToken, selectedTenantId]);

  const fetchTenantData = useCallback(async (forceRefresh = false) => {
    const authenticated = await isAuthenticated();
    // Check if we have a selectedTenantId, if not and the user is authenticated, try to get one from the token
    if (!selectedTenantId && authenticated) {
      const tenantIdFromToken = await getTenantIdFromToken();
      if (tenantIdFromToken) {
        setSelectedTenantId(tenantIdFromToken);
        return;
      }
    }
    
    if (!selectedTenantId) {
      setTenantData(null);
      setLoadingTenant(false);
      if (authenticated) {
        setErrorTenant(new Error('No tenant ID available for fetching data.'));
      } else {
        setErrorTenant(null);
      }
      return;
    }

    setLoadingTenant(true);
    setErrorTenant(null);

    if (!forceRefresh) {
      try {
        const cachedDataString = localStorage.getItem(TENANT_CACHE_KEY);
        if (cachedDataString) {
          const cachedData = JSON.parse(cachedDataString);
          if (cachedData && cachedData.tenant_id === selectedTenantId) {
            setTenantData(cachedData);
            setLoadingTenant(false);
            console.log('Tenant data loaded from localStorage for tenant:', selectedTenantId);
            return;
          }
        }
      } catch (error) {
        console.error("Error reading tenant data from localStorage:", error);
      }
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/app/api/tenant/${selectedTenantId}/`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const fetchedTenantData = response.data;
      setTenantData(fetchedTenantData);
      localStorage.setItem(TENANT_CACHE_KEY, JSON.stringify(fetchedTenantData));
      console.log('Tenant data fetched from API and saved to localStorage for tenant:', selectedTenantId);
    } catch (err) {
      console.error("Failed to fetch tenant data from API for tenant:", selectedTenantId, err);
      setErrorTenant(err);
    } finally {
      setLoadingTenant(false);
    }
  }, [selectedTenantId, getTenantIdFromToken]);

  const clearTenantData = useCallback(() => {
    setTenantData(null);
    setSelectedTenantId(null); // Clear selectedTenantId state
    try {
      localStorage.removeItem(TENANT_CACHE_KEY);
      localStorage.removeItem('selected_tenant_id');
      console.log('Tenant data and selected_tenant_id cleared from localStorage');
    } catch (error) {
      console.error("Error removing tenant data from localStorage:", error);
    }
  }, []);

  const switchTenant = useCallback((newTenantId) => {
    console.log('Switching tenant to:', newTenantId);
    if (!newTenantId || newTenantId === selectedTenantId) return;
    
    try {
      localStorage.setItem('selected_tenant_id', newTenantId);
    } catch (error) {
      console.error("Error saving selected tenant ID to localStorage:", error);
    }
    setSelectedTenantId(newTenantId); // This will trigger the fetch effect
  }, [selectedTenantId]);

  const getAvailableTenants = useCallback(async () => {
    console.log('[getAvailableTenants] Getting available tenants from token or API');
    try {
      const token = getToken();
      if (!token) {
        console.warn('[getAvailableTenants] No access token found');
        return [];
      }
      const decoded = jwtDecode(token);
      console.log('[getAvailableTenants] Decoded token in getAvailableTenants');
      let tenants = decoded && Array.isArray(decoded.tenants) ? decoded.tenants : null;
      console.log('[getAvailableTenants] Tenants from token:', tenants);
      if (!tenants || tenants.length === 0) {
        console.log('[getAvailableTenants] No tenants found in decoded token, fetching from API');
        tenants = await fetchTenantsFromApi();
        console.log('[getAvailableTenants] Fetched tenants from API:', tenants);
      }
      return tenants || [];
    } catch (error) {
      console.error('[getAvailableTenants] Error decoding token or extracting tenants list:', error);
      return [];
    }
  }, []);

  // Effect to load tenant data when selectedTenantId changes or on authentication changes
  useEffect(() => {
    const token = getToken();
    if (token) {
      // If selectedTenantId is null here, it means getTenantIdFromToken resolved to null.
      // fetchTenantData will handle the case where selectedTenantId is null.
      fetchTenantData();
    } else {
      clearTenantData();
    }
  }, [selectedTenantId, fetchTenantData, clearTenantData]);

  // Expose fetchTenantsFromApi
  const contextValue = {
    tenantData,
    loadingTenant,
    errorTenant,
    selectedTenantId,
    fetchTenantData,
    clearTenantData,
    fetchTenantsFromApi, // Added here
    getAvailableTenants, // Added here
    switchTenant,
  };

  return (
    <TenantContext.Provider value={contextValue}>
      {children}
    </TenantContext.Provider>
  );
};
